import { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Swords, AlertCircle } from 'lucide-react';
import { toast } from 'sonner';

/**
 * Login — Colosseum-themed direct Google OAuth entry point.
 *
 * Flow:
 *   1. User lands here, sees the brand-locked entry page.
 *   2. Clicks "Sign in with Google".
 *   3. We mint a random CSRF state, stash it in sessionStorage, and
 *      redirect to Google's OAuth consent screen.
 *   4. Google redirects back to /auth/google?code=...&state=... — that
 *      route is handled by AuthCallback.js which posts the code to the
 *      backend to complete the exchange.
 *
 * REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT
 * URLS, THIS BREAKS THE AUTH. The redirect URI is built from
 * window.location.origin so it's always the exact origin the user is on
 * — which is also the value we whitelist in Google Cloud Console.
 */

const GOOGLE_AUTH_URL = 'https://accounts.google.com/o/oauth2/v2/auth';
const GOOGLE_CLIENT_ID = process.env.REACT_APP_GOOGLE_CLIENT_ID || '';

// In-app webview / iframe detection (Gmail, Messages, FB, Insta, etc.,
// plus any iframe-embedded preview like Emergent's chat preview pane) —
// Google refuses OAuth in all of these and surfaces a 403 "you do not
// have access to this document" error. We catch this upfront and tell
// the user to open in Safari/Chrome at the top level.
const isEmbeddedWebview = () => {
  if (typeof window === 'undefined' || !window.navigator) return false;
  // (1) Iframe context — we're nested inside another origin's frame.
  // Google blocks OAuth in any framed window. Cross-origin access throws,
  // so wrap in try/catch and treat the throw as "yes, we're framed".
  try {
    if (window.top !== window.self) return true;
  } catch {
    return true;
  }
  const ua = window.navigator.userAgent || '';
  if (/FBAN|FBAV|Instagram|Snapchat|TikTok|LinkedIn|Twitter|Line|WeChat|MicroMessenger|WhatsApp|GSA|Pinterest|Reddit|GMail|Gmail|GoogleApp/i.test(ua)) return true;
  if (/iPhone|iPad|iPod/.test(ua) && /AppleWebKit/.test(ua) && !/Safari/.test(ua)) return true;
  if (/Android/.test(ua) && /wv/.test(ua)) return true;
  return false;
};

const Login = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const [error, setError] = useState(null);
  const [webview, setWebview] = useState(false);

  // Surface any error passed through navigation state (e.g., from
  // AuthCallback if Google returned ?error=access_denied).
  useEffect(() => {
    setWebview(isEmbeddedWebview());
    const msg = location.state?.error;
    if (msg) setError(msg);
  }, [location.state]);

  const handleGoogleSignIn = () => {
    if (!GOOGLE_CLIENT_ID) {
      toast.error('Google sign-in is not configured yet. Please contact support.');
      return;
    }
    if (isEmbeddedWebview()) {
      setWebview(true);
      return;
    }

    // CSRF state — random, stored in sessionStorage, validated in
    // AuthCallback when Google redirects us back.
    const state = Math.random().toString(36).slice(2) + Date.now().toString(36);
    try { sessionStorage.setItem('colosseum_oauth_state', state); } catch { /* ignore */ }

    // Also persist where to land after sign-in (default: /dashboard).
    const next = location.state?.next || '/dashboard';
    try { sessionStorage.setItem('colosseum_post_login', next); } catch { /* ignore */ }

    const redirectUri = window.location.origin + '/auth/google';
    const params = new URLSearchParams({
      client_id: GOOGLE_CLIENT_ID,
      redirect_uri: redirectUri,
      response_type: 'code',
      scope: 'openid email profile',
      access_type: 'online',
      include_granted_scopes: 'true',
      prompt: 'select_account',
      state,
    });
    const oauthUrl = `${GOOGLE_AUTH_URL}?${params.toString()}`;
    // Always navigate at the top frame — Google blocks OAuth in iframes.
    // Setting top.location is permitted cross-origin (reading it isn't),
    // so this break-out attempt is safe; we fall back to in-frame if a
    // sandbox prevents it (the iframe detection above also shows the
    // copy-link rescue path).
    try {
      if (window.top && window.top !== window.self) {
        window.top.location.href = oauthUrl;
        return;
      }
    } catch { /* ignore — fall through to in-frame navigation */ }
    window.location.href = oauthUrl;
  };

  return (
    <div className="theme-fixed min-h-screen flex flex-col items-center justify-center bg-[#1C1917] px-4 py-12">
      <div className="w-full max-w-md">
        {/* Brand lockup */}
        <button
          type="button"
          onClick={() => navigate('/')}
          className="flex items-center justify-center gap-3 w-full mb-10 text-[#F4EFE6] hover:text-[#E8C5A0] transition-colors focus:outline-none"
          data-testid="login-brand-link"
          aria-label="Return home"
        >
          <Swords className="w-7 h-7 text-[#D4AF37]" />
          <span className="text-2xl sm:text-3xl font-light italic tracking-tight" style={{fontFamily: 'Cormorant Garamond, serif'}}>
            Welcome to the Colosseum&#8482;
          </span>
        </button>

        {/* Card */}
        <div className="rounded-xl border border-[#3D3531] bg-[#2A2522] p-8 shadow-2xl">
          <p className="text-xs uppercase tracking-[0.4em] text-[#D4AF37] text-center mb-3" style={{fontFamily: 'Work Sans, sans-serif'}}>
            Sign in
          </p>
          <h1 className="text-3xl sm:text-4xl font-light italic text-[#F4EFE6] text-center mb-2" style={{fontFamily: 'Cormorant Garamond, serif'}}>
            {t('auth_pages.welcome_gates_open')}
          </h1>
          <p className="text-sm text-[#9C8E83] text-center mb-8" style={{fontFamily: 'Work Sans, sans-serif'}}>
            {t('auth_pages.stories_scroll_legacy')}
          </p>

          {error && (
            <div className="mb-6 rounded-md border border-[#8B3A2A]/50 bg-[#3D2A1E] px-4 py-3 flex items-start gap-3" data-testid="login-error">
              <AlertCircle className="w-4 h-4 text-[#E8C5A0] shrink-0 mt-0.5" />
              <p className="text-sm text-[#F4EFE6] leading-snug">{error}</p>
            </div>
          )}

          {webview ? (
            <div className="space-y-4" data-testid="webview-warning">
              <div className="rounded-md border border-[#D4AF37]/40 bg-[#1F1B19] px-4 py-3">
                <p className="text-sm text-[#F4EFE6] leading-snug">
                  {t('auth_pages.browser_warning')}
                </p>
                <p className="text-sm text-[#9C8E83] mt-2 leading-snug">
                  Tap the menu (⋮) in this app and choose <span className="text-[#D4AF37]">Open in Chrome</span> or <span className="text-[#D4AF37]">Open in Safari</span>, or copy the link below and paste it into a normal browser tab. Then sign in there.
                </p>
              </div>
              <button
                type="button"
                onClick={() => {
                  navigator.clipboard?.writeText(window.location.origin + '/login')
                    .then(() => toast.success('Link copied. Paste it into Chrome or Safari.'))
                    .catch(() => toast.info(`Open this in Chrome/Safari: ${window.location.origin}/login`));
                }}
                className="w-full h-11 rounded-md border border-[#D4AF37] bg-transparent text-[#D4AF37] hover:bg-[#D4AF37]/10 transition-colors"
                data-testid="copy-login-link-btn"
                style={{fontFamily: 'Work Sans, sans-serif'}}
              >
                {t('auth_pages.copy_signin_link')}
              </button>
            </div>
          ) : (
            <button
              type="button"
              onClick={handleGoogleSignIn}
              className="w-full h-12 rounded-md bg-[#D4AF37] hover:bg-[#E8C5A0] text-[#1C1917] font-medium flex items-center justify-center gap-3 transition-colors shadow-[0_2px_12px_rgba(212,175,55,0.25)] hover:shadow-[0_4px_18px_rgba(212,175,55,0.4)]"
              data-testid="google-signin-btn"
              style={{fontFamily: 'Work Sans, sans-serif'}}
            >
              {/* Google "G" mark — official 4-color SVG */}
              <svg width="18" height="18" viewBox="0 0 18 18" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
                <path fill="#4285F4" d="M17.64 9.2c0-.637-.057-1.251-.164-1.84H9v3.481h4.844a4.14 4.14 0 0 1-1.796 2.717v2.258h2.908c1.702-1.567 2.684-3.874 2.684-6.615z"/>
                <path fill="#34A853" d="M9 18c2.43 0 4.467-.806 5.956-2.184l-2.908-2.258c-.806.54-1.837.86-3.048.86-2.344 0-4.328-1.584-5.036-3.711H.957v2.332A8.997 8.997 0 0 0 9 18z"/>
                <path fill="#FBBC05" d="M3.964 10.707A5.41 5.41 0 0 1 3.682 9c0-.593.102-1.17.282-1.707V4.961H.957A8.996 8.996 0 0 0 0 9c0 1.452.348 2.827.957 4.039l3.007-2.332z"/>
                <path fill="#EA4335" d="M9 3.58c1.321 0 2.508.454 3.44 1.345l2.582-2.58C13.463.891 11.426 0 9 0A8.997 8.997 0 0 0 .957 4.961L3.964 7.293C4.672 5.166 6.656 3.58 9 3.58z"/>
              </svg>
              {t('auth_pages.sign_in_with_google')}
            </button>
          )}

          <p className="text-[11px] text-[#8A817C] text-center mt-6 leading-relaxed" style={{fontFamily: 'Work Sans, sans-serif'}}>
            By signing in you agree to our{' '}
            <button
              type="button"
              onClick={() => navigate('/terms')}
              className="text-[#D4AF37] hover:text-[#E8C5A0] underline underline-offset-2"
              data-testid="login-terms-link"
            >
              {t('auth_pages.terms')}
            </button>
            {' '}and{' '}
            <button
              type="button"
              onClick={() => navigate('/privacy')}
              className="text-[#D4AF37] hover:text-[#E8C5A0] underline underline-offset-2"
              data-testid="login-privacy-link"
            >
              {t('auth_pages.privacy_policy')}
            </button>
            .<br />
            {t('auth_pages.data_storage_note')}
          </p>
        </div>

        <button
          type="button"
          onClick={() => navigate('/')}
          className="block mx-auto mt-6 text-xs text-[#9C8E83] hover:text-[#D4AF37] transition-colors"
          data-testid="login-back-link"
        >
          ← Back to home
        </button>
      </div>
    </div>
  );
};

export default Login;
